import utils from '../utils';

type Banks =
  | 'accidental'
  | 'romanNumeral'
  | 'figuredBass'
  | 'scaleDegree'
  | 'symbol'
  | 'rhythm'
  | 'quality';

// Yes, this is rediculous, but it helps with autocomplete.
export type FontKey =
  | 'x'
  | '#'
  | 'natural'
  | 'b'
  | 'bb'
  | 'n'
  | 'I'
  | 'i'
  | 'V'
  | 'v'
  | 'Maj'
  | 'M'
  | 'min'
  | 'm'
  | 'half-dim'
  | 'Aug'
  | 'A'
  | 'Dom'
  | '+'
  | 'dim'
  | 'o'
  | 'o/'
  | '53'
  | ''
  | '63'
  | '6'
  | '64'
  | '753'
  | '7'
  | '653'
  | '65'
  | '643'
  | '43'
  | '642'
  | '42'
  | '^1'
  | '^2'
  | '^3'
  | '^4'
  | '^5'
  | '^6'
  | '^7'
  | 'trash'
  | '_'
  | 'check'
  | 'checkmark'
  | 'checkMark'
  | 'up'
  | 'down'
  | 'left'
  | 'right'
  | 'b'
  | 'w'
  | 'h'
  | 'q'
  | 'e'
  | '8'
  | 's'
  | '16'
  | '32'
  | 'b.'
  | 'w.'
  | 'h.'
  | 'q.'
  | 'e.'
  | '8.'
  | 's.'
  | '16.'
  | '32.'
  | 'br'
  | 'wr'
  | 'hr'
  | 'qr'
  | 'er'
  | '8r'
  | 'sr'
  | '16r'
  | '32r'
  | 'b.r'
  | 'w.r'
  | 'h.r'
  | 'q.r'
  | 'e.r'
  | '8.r'
  | 's.r'
  | '16.r'
  | '32.r';

const font: {
  /* shorthand by which we search */
  key: string;
  /* the character in the font */
  character: string;

  bank: Banks;
  alt: string;
  unicode?: number;
}[] = [
  // Accidentals:
  { key: '', character: '', bank: 'accidental', alt: '' },
  { key: 'x', character: 'x', bank: 'accidental', alt: 'double-sharp' },
  { key: '#', character: '#', bank: 'accidental', alt: 'sharp' },
  { key: 'n', character: 'n', bank: 'accidental', alt: 'natural' },
  { key: 'natural', character: 'n', bank: 'accidental', alt: 'natural' },
  { key: 'b', character: 'b', bank: 'accidental', alt: 'flat' },
  { key: 'bb', character: 'v', bank: 'accidental', alt: 'double-flat' },
  { key: 'v', character: 'v', bank: 'accidental', alt: 'double-flat' },

  // Roman Numerals:
  { key: '', character: '', bank: 'romanNumeral', alt: '' },
  { key: 'I', character: 'I', bank: 'romanNumeral', alt: 'I' },
  { key: 'i', character: 'i', bank: 'romanNumeral', alt: 'i' },
  { key: 'V', character: 'V', bank: 'romanNumeral', alt: 'V' },
  { key: 'v', character: '√', unicode: 8730, bank: 'romanNumeral', alt: 'v' },

  // Qualities of Chords & Intervals:
  { key: '', character: '', bank: 'quality', alt: '' },
  { key: 'Maj', character: 'M', bank: 'quality', alt: 'Major' }, // Major
  { key: 'M', character: 'Â', unicode: 194, bank: 'quality', alt: 'Major' }, // M
  { key: 'min', character: 'm', bank: 'quality', alt: 'minor' }, // minor
  { key: 'm', character: 'µ', unicode: 181, bank: 'quality', alt: 'minor' }, // m
  { key: 'half-dim', character: '½', unicode: 189, bank: 'quality', alt: 'half-diminished' }, // half-diminished
  { key: 'Aug', character: 'a', bank: 'quality', alt: 'Augmented' }, // augmented
  { key: 'A', character: 'a', bank: 'quality', alt: 'Augmented' }, // augmented
  { key: 'Dom', character: 'Î', unicode: 206, bank: 'quality', alt: 'Dominant' },
  { key: '+', character: '+', bank: 'quality', alt: 'Augmented' }, // Augmented
  { key: 'dim', character: 'd', bank: 'quality', alt: 'diminished' }, // diminished
  { key: 'o', character: 'o', bank: 'quality', alt: 'diminished' }, // diminished
  { key: 'o/', character: 'ø', unicode: 248, bank: 'quality', alt: 'half-diminished' }, // half-diminished symbol

  // Figured Bass Symbols:
  // Shift + Inversion(1 or 2) = Triad shorthand
  // Alt + Inversion(or R for root) = Triad longhand
  // For seventh chords, use 7, 8, 9, 0 for the inversions...

  { key: '53', character: 'R', bank: 'figuredBass', alt: '5/3' },
  { key: '', character: '', bank: 'figuredBass', alt: '' },
  { key: '63', character: '¡', unicode: 161, bank: 'figuredBass', alt: '6/3' },
  { key: '6', character: '!', unicode: 33, bank: 'figuredBass', alt: '6' },
  { key: '64', character: '@', bank: 'figuredBass', alt: '6/4' }, // shift-2
  { key: '753', character: '¶', unicode: 182, bank: 'figuredBass', alt: '7/5/3' },
  { key: '7', character: '&', bank: 'figuredBass', alt: '7' },
  { key: '653', character: '•', unicode: 8226, bank: 'figuredBass', alt: '6/5/3' },
  { key: '65', character: '*', bank: 'figuredBass', alt: '6/5' },
  { key: '643', character: 'ª', unicode: 170, bank: 'figuredBass', alt: '6/4/3' },
  { key: '43', character: '(', bank: 'figuredBass', alt: '4/3' },
  { key: '642', character: 'º', unicode: 186, bank: 'figuredBass', alt: '6/4/2' },
  { key: '42', character: ')', bank: 'figuredBass', alt: '4/2' },

  // Scale Degrees:
  { key: '', character: '', bank: 'scaleDegree', alt: '' },
  { key: '^1', character: '1', bank: 'scaleDegree', alt: '1' },
  { key: '^2', character: '2', bank: 'scaleDegree', alt: '2' },
  { key: '^3', character: '3', bank: 'scaleDegree', alt: '3' },
  { key: '^4', character: '4', bank: 'scaleDegree', alt: '4' },
  { key: '^5', character: '5', bank: 'scaleDegree', alt: '5' },
  { key: '^6', character: '6', bank: 'scaleDegree', alt: '6' },
  { key: '^7', character: '7', bank: 'scaleDegree', alt: '7' },

  // Special Symbols:
  { key: '', character: '', bank: 'symbol', alt: '' },
  { key: 'trash', character: '†', unicode: 8224, bank: 'symbol', alt: 'delete' }, // alt+t for "trash"
  { key: '_', character: '_', bank: 'symbol', alt: 'blank' },
  { key: 'check', character: 'c', bank: 'symbol', alt: 'check' },
  { key: 'checkmark', character: 'c', bank: 'symbol', alt: 'checkmark' },
  { key: 'checkMark', character: 'c', bank: 'symbol', alt: 'checkmark' },
  { key: 'up', character: '∑', unicode: 8721, bank: 'symbol', alt: 'up-arrow' }, // alt w
  { key: 'down', character: 'ß', unicode: 223, bank: 'symbol', alt: 'down-arrow' }, // alt s
  { key: 'left', character: 'å', unicode: 229, bank: 'symbol', alt: 'left-arrow' }, // alt a
  { key: 'right', character: '∂', unicode: 8706, bank: 'symbol', alt: 'right-arrow' }, // alt d

  // Rhythm Font Codes:
  { key: '', character: '', bank: 'rhythm', alt: '' },
  { key: 'b', character: '1', bank: 'rhythm', alt: 'breve' },
  { key: 'w', character: '2', bank: 'rhythm', alt: 'whole note' },
  { key: 'h', character: '3', bank: 'rhythm', alt: 'half note' },
  { key: 'q', character: '4', bank: 'rhythm', alt: 'quarter note' },
  { key: 'e', character: '5', bank: 'rhythm', alt: 'eighth note' },
  { key: '8', character: '5', bank: 'rhythm', alt: 'eighth note' },
  { key: 's', character: '6', bank: 'rhythm', alt: 'sixteenth note' },
  { key: '16', character: '6', bank: 'rhythm', alt: 'sixteenth note' },
  { key: '32', character: '7', bank: 'rhythm', alt: '32nd note' },

  { key: 'b.', character: '1.', bank: 'rhythm', alt: 'dotted-breve' },
  { key: 'w.', character: '2.', bank: 'rhythm', alt: 'dotted-whole note' },
  { key: 'h.', character: '3.', bank: 'rhythm', alt: 'dotted-half note' },
  { key: 'q.', character: '4.', bank: 'rhythm', alt: 'dotted-quarter note' },
  { key: 'e.', character: '5.', bank: 'rhythm', alt: 'dotted-eighth note' },
  { key: '8.', character: '5.', bank: 'rhythm', alt: 'dotted-eighth note' },
  { key: 's.', character: '6.', bank: 'rhythm', alt: 'dotted-sixteenth note' },
  { key: '16.', character: '6.', bank: 'rhythm', alt: 'dotted-sixteenth note' },
  { key: '32.', character: '7.', bank: 'rhythm', alt: 'dotted-32nd note' },

  { key: 'br', character: '!', bank: 'rhythm', alt: 'breve-rest' },
  { key: 'wr', character: '@', bank: 'rhythm', alt: 'whole-rest' },
  { key: 'hr', character: '#', bank: 'rhythm', alt: 'half-rest' },
  { key: 'qr', character: '$', bank: 'rhythm', alt: 'quarter-rest' },
  { key: 'er', character: '%', bank: 'rhythm', alt: 'eighth-rest' },
  { key: '8r', character: '%', bank: 'rhythm', alt: 'eighth-rest' },
  { key: 'sr', character: '^', bank: 'rhythm', alt: 'sixteenth-rest' },
  { key: '16r', character: '^', bank: 'rhythm', alt: 'sixteenth-rest' },
  { key: '32r', character: '&', bank: 'rhythm', alt: '32nd-note-rest' },

  { key: 'b.r', character: '!>', bank: 'rhythm', alt: 'dotted-breve-rest' },
  { key: 'w.r', character: '@>', bank: 'rhythm', alt: 'dotted-whole-rest' },
  { key: 'h.r', character: '#>', bank: 'rhythm', alt: 'dotted-half-rest' },
  { key: 'q.r', character: '$>', bank: 'rhythm', alt: 'dotted-quarter-rest' },
  { key: 'e.r', character: '%>', bank: 'rhythm', alt: 'dotted-eighth-rest' },
  { key: '8.r', character: '%>', bank: 'rhythm', alt: 'dotted-eighth-rest' },
  { key: 's.r', character: '^>', bank: 'rhythm', alt: 'dotted-sixteenth-rest' },
  { key: '16.r', character: '^>', bank: 'rhythm', alt: 'dotted-sixteenth-rest' },
  { key: '32.', character: '&>', bank: 'rhythm', alt: 'dotted-32nd-rest' },
];

// const types: string[] = utils.unique(font.filter(char => char.bank).map(char => char.bank));

/*
export function find(stringKey: string | number, type: string | undefined = undefined) {
  if (stringKey === '') return '';
  if (type !== undefined && !types.includes(type)) {
    throw new Error(`Character type ${type} not found, must be one of ${types.join(', ')}`);
  }
  const def = Library.find(character => {
    const typeMatch = !type || character.bank === type;
    // eslint-disable-next-line eqeqeq
    const keyMatch = `${stringKey}` === character.key;
    return typeMatch && keyMatch;
  });

  if (typeof def !== 'undefined') {
    if (typeof def.n !== 'undefined') {
      return String.fromCharCode(def.n);
    }
    return def.c;
  }
  return false;
}
*/

export function findCharacter(bank: Banks, key: FontKey) {
  const character = font.find((c) => c.bank === bank && c.key === key);
  if (!character) throw Error(`No character found for ${bank}: ${key}`);
  const keyString = character.unicode
    ? String.fromCharCode(character.unicode)
    : character.character;

  return {
    string: keyString,
    aria: character.alt,
  };
}

export interface ToHTMLOptions {
  /* add a space in the aria span first, default false */
  ariaSpaceBefore?: boolean;
  /* add a space in the aria span after, default false */
  ariaSpaceAfter?: boolean;
  /* add a space between keys in the aria span, default false */
  ariaSpaceBetween?: boolean;
}

/**
 *
 * @param bank group in which to search -- accidentals, rhythm, etc...
 * @param keys specific character key
 */
export function toHTML(
  bank: Banks,
  keys: FontKey | FontKey[],
  { ariaSpaceBefore, ariaSpaceAfter, ariaSpaceBetween }: ToHTMLOptions = {}
) {
  if (!utils.isArray(keys)) {
    keys = [keys];
  }
  const { strings, ariaStrings } = keys.reduce(
    // eslint-disable-next-line @typescript-eslint/no-shadow
    ({ strings, ariaStrings }, key: FontKey) => {
      const character = font.find((c) => c.bank === bank && c.key === key);
      if (!character) throw Error(`No character found for ${bank}: ${key}`);

      if (character.key === '' && character.character === '') return { strings, ariaStrings };

      const keyString = character.unicode
        ? String.fromCharCode(character.unicode)
        : character.character;

      strings.push(keyString);
      ariaStrings.push(character.alt);

      return { strings, ariaStrings };
    },
    { strings: [], ariaStrings: [] } as { strings: string[]; ariaStrings: string[] }
  );

  const string = strings.join('');
  const ariaString = ariaStrings.join(ariaSpaceBetween ? ' ' : '');
  if (!string && !ariaString) return '';

  // const string = character.unicode ? String.fromCharCode(character.unicode) : character.character;
  const className = bank === 'rhythm' ? 'rhythm' : 'accidentals';

  const visibleSpan = `<span class='${className}' aria-hidden='true'>${string}</span>`;
  const srOnlySpan = `<span class='sr-only'>${ariaSpaceBefore ? ' ' : ''}${ariaString}${
    ariaSpaceAfter ? ' ' : ''
  }</span>`;

  return visibleSpan + srOnlySpan;
}
